import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Autocomplete,
    TextField,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import AppForm from "components/Shared/AppForm";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useLazySearchShipmentWithBranchQuery } from "store/services/shipmentApi";
import { useCreateBulkShipmentNotesMutation } from "store/services/noteApi";
import {
    useLazyGetVendorProfilesByVendorIdQuery,
    useLazyGetVendorProfilesByVendorStaffIdQuery,
} from "store/services/vendorProfileApi";
import { useAuth } from "hooks";

const NOTE_TYPES = ["Note", "Call", "Meeting", "Email", "Other"];

const BulkShipmentNoteModalForm = ({ open, onClose, accountId, entityId }) => {
    const { isVendor, isVendorStaff, isCustomer, isDriver, isContractor, loggedInUser } = useAuth();
    const isRestrictedRole = isVendor || isVendorStaff;
    const typeRefId = loggedInUser?.typeRef;

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [triggerSearch, { data: shipments = [], isFetching: loadingShipments }] =
        useLazySearchShipmentWithBranchQuery();

    const [
        triggerGetVendorProfiles,
        { data: vendorProfilesData },
    ] = useLazyGetVendorProfilesByVendorIdQuery();

    const [
        triggerGetVendorStaffProfiles,
        { data: vendorStaffProfilesData },
    ] = useLazyGetVendorProfilesByVendorStaffIdQuery();

    const [createBulkNotes, { isLoading: isSubmitting }] = useCreateBulkShipmentNotesMutation();

    // Unified vendor profile IDs for shipment filter
    const vendorProfileIds = isVendor
        ? vendorProfilesData?.data?.map((p) => p._id) ?? []
        : isVendorStaff
        ? vendorStaffProfilesData?.data?.map((p) => p._id) ?? []
        : undefined;

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            shipments: [],
            content: "",
            type: "Note",
            showClient: isCustomer || isRestrictedRole || isDriver || isContractor,
        },
    });

    const selectedShipments = watch("shipments");

    // Fetch profiles on mount for restricted roles
    useEffect(() => {
        if (!typeRefId) return;
        if (isVendor) triggerGetVendorProfiles(typeRefId);
        else if (isVendorStaff) triggerGetVendorStaffProfiles(typeRefId);
    }, [isVendor, isVendorStaff, typeRefId, triggerGetVendorProfiles, triggerGetVendorStaffProfiles]);

    // Debounced search
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((term, profileIds) => {
            if (accountId && entityId) {
                triggerSearch({
                    account: accountId,
                    entity: entityId,
                    query: term || "",
                    vendorProfileIds: profileIds,
                });
            }
        }, 400),
        [accountId, entityId, triggerSearch]
    );

    // Fire on input change — restricted roles wait until profiles are resolved
    useEffect(() => {
        if (!open || !accountId || !entityId) return;
        if (isRestrictedRole && !vendorProfileIds?.length) return;
        debouncedSearch(searchInput, vendorProfileIds);
        return () => debouncedSearch.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput, debouncedSearch, open, accountId, entityId, vendorProfileIds]);

    // Reset on modal open
    useEffect(() => {
        if (open) {
            reset({
                shipments: [],
                content: "",
                type: "Note",
                showClient: isCustomer || isRestrictedRole || isDriver || isContractor,
            });
            setSearchInput("");
            setError("");
            setSuccess("");

            // Admin only: fire initial search immediately
            // Restricted roles: deferred via vendorProfileIds effect chain
            if (!isRestrictedRole) {
                triggerSearch({ account: accountId, entity: entityId, query: "" });
            }
        }
    }, [open, accountId, entityId, reset, triggerSearch, isRestrictedRole, isCustomer, isDriver, isContractor]);

    const onSubmit = async (data) => {
        if (!data.shipments.length) {
            setError("Please select at least one shipment.");
            return;
        }
        if (
            !data.content ||
            data.content === "<p>Enter note here.</p>" ||
            data.content.trim() === "<p></p>"
        ) {
            setError("Please provide note content.");
            return;
        }
        setError("");
        try {
            const bulkNoteData = {
                shipmentIds: data.shipments.map((s) => s._id),
                content: data.content,
                type: data.type,
                showClient:
                    isCustomer || isRestrictedRole || isDriver || isContractor
                        ? true
                        : data.showClient,
            };
            await createBulkNotes(bulkNoteData).unwrap();
            setSuccess(`Note added to ${data.shipments.length} shipment(s) successfully!`);
            setTimeout(() => {
                onClose();
                reset();
            }, 1500);
        } catch (err) {
            setError(err?.data?.message || err?.message || "Failed to create bulk notes");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, py: 2 }}>
                Add Note to Multiple Shipments
            </DialogTitle>
            <Divider />
            <AppForm loading={isSubmitting}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent sx={{ p: 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Select Shipments
                            </Typography>
                            <Controller
                                name="shipments"
                                control={control}
                                rules={{ required: "Select at least one shipment" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={shipments?.data || []}
                                        loading={loadingShipments}
                                        value={field.value ?? []}
                                        onChange={(_, value) => field.onChange(value)}
                                        onInputChange={(_, newInput) => setSearchInput(newInput)}
                                        getOptionLabel={(opt) => `${opt.shipmentId}`}
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Search Shipments"
                                                placeholder="Type shipment ID to search..."
                                                variant="outlined"
                                                error={!!errors.shipments}
                                                helperText={
                                                    errors.shipments?.message ||
                                                    `${field.value?.length ?? 0} shipment(s) selected`
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Note Content
                            </Typography>
                            <Controller
                                name="content"
                                control={control}
                                rules={{ required: "Please provide the note content." }}
                                render={({ field: { onChange } }) => (
                                    <RichTextEditor
                                        initialValue="<p>Enter note here.</p>"
                                        onChange={onChange}
                                        height={200}
                                    />
                                )}
                            />
                            {errors.content && (
                                <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                                    {errors.content.message}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="note-type-label">Type</InputLabel>
                                        <Select labelId="note-type-label" label="Type" {...field}>
                                            {NOTE_TYPES.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Box>
                        {!isCustomer && !isRestrictedRole && !isContractor && !isDriver && (
                            <Box>
                                <Controller
                                    name="showClient"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={field.value}
                                                    {...field}
                                                />
                                            }
                                            label="Show to Client"
                                        />
                                    )}
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!selectedShipments?.length || isSubmitting}
                        >
                            {isSubmitting ? "Adding Notes..." : "Add Note to Shipments"}
                        </Button>
                    </DialogActions>
                </form>
            </AppForm>
        </Dialog>
    );
};

export default BulkShipmentNoteModalForm;